import type {ReactElement} from 'react';
import {useContext, useState} from 'react';
import {Modal} from '@Components/modal';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {uploadCustomHtmlIntoEditor} from '@EmailMakerReact/lib/email-maker-admin-tools-library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {ADMIN_UPLOAD_HTML_PANEL_ID, ADMIN_UPLOAD_HTML_TITLE, ADMIN_UPLOAD_HTML_UPLOAD_LABEL} from './email-maker-admin-upload-html-modal.types';
import styles from './email-maker-admin-upload-html-modal.module.scss';

/** Pre-mounted inside the editor context (reads the live editor); opened via openPanelInModal on the admin button. */
export function EmailMakerAdminUploadHtmlModal(): ReactElement | null {
  const {editor} = useContext(emailMakerContext);
  const dispatch = useAppDispatch();
  const isOpen = useAppSelector((s) => s.modals.modalsHashmap[MODALS_IDS.EMAIL_MAKER_ADMIN_UPLOAD_HTML_MODAL]?.isOpen ?? false);
  const [html, setHtml] = useState('');

  if (!isOpen || !editor) {
    return null;
  }

  function handleClose(): void {
    dispatch(closeModal(MODALS_IDS.EMAIL_MAKER_ADMIN_UPLOAD_HTML_MODAL));
  }

  function handleUpload(): void {
    if (editor) {
      uploadCustomHtmlIntoEditor(editor, html);
    }
    handleClose();
  }

  return (
    <Modal
      modalId={MODALS_IDS.EMAIL_MAKER_ADMIN_UPLOAD_HTML_MODAL}
      panelId={ADMIN_UPLOAD_HTML_PANEL_ID}
      modalHeight="fit-content"
      modalMaxHeight="90vh"
      modalWidth="100%"
      modalMaxWidth="640px"
    >
      <Panel
        panelHeader={<PanelHeader title={ADMIN_UPLOAD_HTML_TITLE} onClose={handleClose} />}
        panelContent={
          <PanelContent>
            <div className={styles.content}>
              <textarea
                aria-label={ADMIN_UPLOAD_HTML_TITLE}
                className={styles.textarea}
                onChange={(e): void => {
                  setHtml(e.target.value);
                }}
                value={html}
              />
              <Button type={ButtonType.PRIMARY} size={ButtonSize.LARGE} text={ADMIN_UPLOAD_HTML_UPLOAD_LABEL} onClick={handleUpload} />
            </div>
          </PanelContent>
        }
      />
    </Modal>
  );
}
